import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Spinner, Button, Badge, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import PaymentTimeline from "../Components/PaymentTimeline";
import RecordPaymentModal from "../Components/RecordPaymentModal";
import { paymentsApi } from "../Components/paymentsApi";
import { toast } from "react-toastify";
import { CurrencyContext } from "../Contexts/CurrencyContext";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import styles from "./AccountsPayments.module.scss";

const defaultStageRow = () => ({ due_date: "", amount: "" });

export default function PaymentPlanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = useContext(CurrencyContext);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [enquiry, setEnquiry] = useState(null);
  const [stages, setStages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modalStage, setModalStage] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [sessionEndDate, setSessionEndDate] = useState("");
  const [stageRows, setStageRows] = useState([defaultStageRow()]);
  const [addingStages, setAddingStages] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await paymentsApi.getPlan(id);
      setPlan(data?.plan ?? null);
      setEnquiry(data?.enquiry ?? null);
      const stagesRes = await paymentsApi.listStages(id);
      setStages(stagesRes?.stages ?? []);
      const setRes = await paymentsApi.getSettings();
      setSettings(setRes ?? null);
    } catch (e) {
      toast.error(e.message || "Failed to load payment plan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (plan?.session_end_date && stages.length === 0) setSessionEndDate(plan.session_end_date);
  }, [plan?.session_end_date, stages.length]);

  const handleRecordPayment = (stage) => {
    setModalStage(stage);
    setModalShow(true);
  };

  const handlePaymentSuccess = () => {
    setModalStage(null);
    setModalShow(false);
    load();
  };

  const addStageRow = () => setStageRows((prev) => [...prev, defaultStageRow()]);
  const removeStageRow = (index) =>
    setStageRows((prev) => prev.filter((_, i) => i !== index));
  const updateStageRow = (index, field, value) =>
    setStageRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );

  const handleAddStages = async (e) => {
    e.preventDefault();
    if (!plan?.id) return;
    const sessionEnd = sessionEndDate.trim();
    if (!sessionEnd) {
      toast.error("Session end date is required so all payments are due before the session ends.");
      return;
    }
    const built = stageRows
      .map((r) => ({
        due_date: r.due_date?.trim(),
        amount: r.amount !== "" && r.amount != null ? parseFloat(r.amount) : null,
      }))
      .filter((r) => r.due_date && r.amount != null && !Number.isNaN(r.amount));
    if (built.length === 0) {
      toast.error("Add at least one stage with due date and amount.");
      return;
    }
    const totalEntered = built.reduce((sum, r) => sum + r.amount, 0);
    const planTotal = Number(plan.total_amount);
    if (Math.abs(totalEntered - planTotal) > 0.01) {
      toast.error(`Stages total (${formatCurrency(totalEntered)}) must equal the plan total (${formatCurrency(planTotal)}).`);
      return;
    }
    const sessionEndDateObj = new Date(sessionEnd);
    const invalidDue = built.find((r) => new Date(r.due_date) > sessionEndDateObj);
    if (invalidDue) {
      toast.error(`All due dates must be on or before the session end date (${sessionEnd}).`);
      return;
    }
    setAddingStages(true);
    try {
      await paymentsApi.createStages({
        plan_id: plan.id,
        stages: built.map((r) => ({ due_date: r.due_date, amount: r.amount })),
        session_end_date: sessionEnd,
      });
      toast.success("Payment stages added.");
      setStageRows([defaultStageRow()]);
      setSessionEndDate("");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to add stages.");
    } finally {
      setAddingStages(false);
    }
  };

  const requireReceipt = settings?.require_receipt_upload === true;
  const stagesTotal = stageRows.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const planTotalNum = plan ? Number(plan.total_amount) : 0;
  const totalMatches = Math.abs(stagesTotal - planTotalNum) < 0.01;

  return (
    <div className={styles.root}>
            <div className={`${styles.mainContent} ${styles.contentPaper}`}>
              <button
                type="button"
                className={styles.backLink}
                onClick={() => navigate("/accounts/payments")}
              >
                <ArrowBackIcon fontSize="small" /> Back to Payments
              </button>

              {loading ? (
                <div className={styles.loadingContainer}>
                  <Spinner animation="border" />
                </div>
              ) : !plan ? (
                <div className={styles.emptyState}>
                  <p className="mb-0">Plan not found.</p>
                </div>
              ) : (
                <>
                  <h1 className={styles.pageTitle}>Payment Plan</h1>
                  {enquiry && (
                    <p className={`${styles.pageSubtitle} mb-3`}>
                      {enquiry.first_name} {enquiry.last_name} · Enquiry #{enquiry.id}
                    </p>
                  )}
                  <div className={styles.badgesWrap}>
                    <Badge bg="secondary">Total: {formatCurrency(plan.total_amount)}</Badge>
                    <Badge bg="info">{plan.status}</Badge>
                    {plan.session_end_date && (
                      <Badge bg="dark">Session end: {plan.session_end_date}</Badge>
                    )}
                  </div>

                  {stages.length === 0 ? (
                    <div className={`${styles.planCard} mt-4`}>
                      <div className={styles.planCardHeader}>
                        <h5 className={styles.planCardTitle}>Add payment stages</h5>
                        <p className={styles.planCardSubtext}>
                          Add installments with amount and due date. All due dates must be on or before the session end date. The stages total must equal the plan total ({formatCurrency(plan.total_amount)}).
                        </p>
                      </div>
                      <div className={styles.planCardBody}>
                        <Form onSubmit={handleAddStages}>
                          <Form.Group className="mb-3">
                            <Form.Label>Session end date</Form.Label>
                            <Form.Control
                              type="date"
                              value={sessionEndDate}
                              onChange={(e) => setSessionEndDate(e.target.value)}
                              required
                            />
                            <Form.Text className="text-muted">All payment due dates must be on or before this date.</Form.Text>
                          </Form.Group>
                          <div className="mb-2 d-flex justify-content-between align-items-center">
                            <Form.Label className="mb-0">Stages (amount due · date due)</Form.Label>
                            <Button type="button" variant="outline-primary" size="sm" onClick={addStageRow}>
                              <AddCircleOutlineIcon fontSize="small" className="me-1" /> Add stage
                            </Button>
                          </div>
                          {stageRows.map((row, index) => (
                            <div key={index} className="d-flex flex-wrap align-items-center gap-2 mb-2">
                              <Form.Control
                                type="date"
                                value={row.due_date}
                                onChange={(e) => updateStageRow(index, "due_date", e.target.value)}
                                style={{ width: "12rem" }}
                              />
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Amount"
                                value={row.amount}
                                onChange={(e) => updateStageRow(index, "amount", e.target.value)}
                                style={{ width: "10rem" }}
                              />
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeStageRow(index)}
                                disabled={stageRows.length <= 1}
                                aria-label="Remove stage"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </Button>
                            </div>
                          ))}
                          <div className="mt-3 mb-3">
                            <Badge bg={totalMatches ? "success" : "warning"}>
                              Stages total: {formatCurrency(stagesTotal)}
                              {totalMatches ? " ✓ Matches plan total" : ` (must equal ${formatCurrency(planTotalNum)})`}
                            </Badge>
                          </div>
                          <Button type="submit" variant="primary" disabled={addingStages}>
                            {addingStages ? "Adding…" : "Save payment stages"}
                          </Button>
                        </Form>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h5 className={`${styles.sectionTitle} mt-4`}>Timeline</h5>
                      <PaymentTimeline
                        stages={stages}
                        onRecordPayment={handleRecordPayment}
                        requireReceipt={requireReceipt}
                        formatCurrency={formatCurrency}
                      />
                    </>
                  )}
                </>
              )}
            </div>

            <RecordPaymentModal
              show={modalShow}
              onHide={() => { setModalShow(false); setModalStage(null); }}
              stageId={modalStage?.id}
              enquiryId={enquiry?.id}
              planId={plan?.id}
              stageNumber={modalStage?.stage_number}
              maxAmount={modalStage ? Number(modalStage.amount) - Number(modalStage.paid_amount || 0) : undefined}
              requireReceipt={requireReceipt}
              onSuccess={handlePaymentSuccess}
            />
    </div>
  );
}
